import clsx from 'clsx'
import map from 'lodash/map'
import React, { FC, useMemo } from 'react'

import { TileProps, TilesBlockSettings } from '@Blocks/TilesBlock'
import { TileCard } from '@Blocks/TilesBlock/TileCard'
import { SliderWithArrowsAndIndicator } from '@Components/Slider/SliderWithArrowsAndIndicator'
import { PagePresentation } from '@Generated/graphql'

import styles from './Tiles.module.scss'

export interface TilesProps {
  items: TileProps[]
  presentation?: PagePresentation
  settings?: TilesBlockSettings
}

interface ItemsContainerProps {
  cardSize: string
  children: React.ReactNode
  enableSlider: boolean
  rowsDesktop?: number | string
  rowsMobile?: number | string
}

const generateStyleVars = (rowsMobile: number | string, rowsDesktop: number | string): React.CSSProperties => {
  const styleVars: React.CSSProperties = {}

  if (typeof rowsMobile === 'string' && rowsMobile !== 'auto') {
    styleVars['--grid-template-rows' as any] = `repeat(${rowsMobile}, 1fr)`
  } else if (typeof rowsMobile === 'number' && rowsMobile !== 1) {
    styleVars['--grid-template-rows' as any] = `repeat(${rowsMobile}, 1fr)`
  }

  if (typeof rowsDesktop === 'string' && rowsDesktop !== 'auto') {
    styleVars['--grid-template-rows-desktop' as any] = `repeat(${rowsDesktop}, 1fr)`
  } else if (typeof rowsDesktop === 'number' && rowsDesktop !== 1) {
    styleVars['--grid-template-rows-desktop' as any] = `repeat(${rowsDesktop}, 1fr)`
  }

  return styleVars
}

const ItemsContainer: FC<ItemsContainerProps> = (
  { cardSize,
    children,
    enableSlider,
    rowsDesktop = 'auto',
    rowsMobile = 'auto' },
) => {
  const shouldUseColumnFlowMobile = rowsMobile !== 'auto' && rowsMobile !== 1
  const shouldUseColumnFlowDesktop = rowsDesktop !== 'auto' && rowsDesktop !== 1

  const styleVars = generateStyleVars(rowsMobile, rowsDesktop)

  return (enableSlider
    ? (
      <SliderWithArrowsAndIndicator
        hideArrows
        sliderClassName={clsx(
          styles.slider,
          shouldUseColumnFlowMobile && styles.columnFlowMobile,
          shouldUseColumnFlowDesktop && styles.columnFlowDesktop,
          styles[cardSize],
        )}
        style={
          styleVars
        }
      >
        {children}
      </SliderWithArrowsAndIndicator>
    )
    : (
      <div
        className={clsx(
          styles.list,
          shouldUseColumnFlowMobile && styles.columnFlowMobile,
          shouldUseColumnFlowDesktop && styles.columnFlowDesktop,
          styles[cardSize],
        )}
        style={styleVars}
      >
        {children}
      </div>
    ))
}

export const Tiles: FC<TilesProps> = (
  {
    items,
    presentation,
    settings,
  },
) => {
  const {
    cardAspectRatio = 'square',
    cardSize = 'medium',
    cardVariant = 'background',
    enableSlider = false,
    rowsDesktop = 'auto',
    rowsMobile = 'auto',
  } = settings || {}

  const width = useMemo(() => {
    if (cardVariant === 'spot' && cardSize === 'tiny') {
      return 80
    }

    if (cardVariant === 'spot') {
      return 120
    }

    if (cardSize === 'medium') {
      return 370
    }

    if (cardSize === 'half') {
      return 1024
    }

    if (cardSize === 'tiny') {
      return 60
    }

    return 370
  }, [cardSize, cardVariant])

  const height = useMemo(
    () => {
      if (cardAspectRatio === 'portrait') {
        return width * 1.5
      }

      return width
    },
    [cardAspectRatio, width],
  )

  return (
    <div className={styles.container}>
      <ItemsContainer
        cardSize={cardSize}
        enableSlider={enableSlider}
        rowsDesktop={rowsDesktop}
        rowsMobile={rowsMobile}
      >
        {map(items, (item, i) => {
          if (!item.image?.url && !item?.appearance?.asset) {
            return null
          }

          return (
            <a
              key={`${item.title}_${i}`}
              aria-label={item.title}
              className={styles.slide}
              href={item.button?.url || '#'}
              rel="noopener noreferrer"
              target={item.button?.newTab ? '_blank' : '_self'}
            >
              <TileCard
                key={`TilesWithText_${item.title}`}
                appearance={item.appearance}
                buttonLabel={item.button?.text || ''}
                campaignTag={item.campaignTag}
                cardAspectRatio={cardAspectRatio}
                cardSize={cardSize}
                cardVariant={cardVariant}
                disableLinks
                elements={item.elements}
                height={height}
                image={item.image?.url}
                presentation={presentation || PagePresentation.Full}
                subTitle={item.subTitle}
                title={item.title}
                width={width}
              />
            </a>
          )
        })}
      </ItemsContainer>
    </div>
  )
}

